<!--
  NOTE: This component now uses the new GraphqlDataTable pattern for payment requests.
  It replaces the old BaseDataTable with GraphqlDataTable components for better performance and consistency.
-->
<template>
  <PageHeader>
    <template #header-title>{{ $t('Payment requests') }}</template>
    <template #header-description>
      {{ $t('Access and manage time and expense details for worker payments.') }}
    </template>
  </PageHeader>

  <WdsTabs v-model:active-tab-key="activeTabKey" class="tw-mb-4">
    <WdsTab
      :tab-key="'to-be-payrolled'"
      title="To be payrolled"
      data-test="payment-requests-to-be-payrolled-tab"
    >
      <template #title>
        <WdsStack align-center gap-sm>
          {{ $t('To be payrolled') }}
          <WdsLabel
            v-if="toBePayrolledCount > 0"
            xs
            data-test="payment-request-to-be-payrolled-count-badge"
          >
            {{ toBePayrolledCount }}
          </WdsLabel>
        </WdsStack>
      </template>
      <div class="tw-mt-2">
        <PaymentRequestsToBePayrolledTable
          :is-active-tab="activeTabKey === 'to-be-payrolled'"
          @payment-request-updated="handlePaymentRequestUpdated"
        />
      </div>
    </WdsTab>
    <WdsTab :tab-key="'processing'" title="Processing" data-test="payment-requests-processing-tab">
      <template #title>
        <WdsStack align-center gap-sm>
          {{ $t('Processing') }}
          <WdsLabel
            v-if="processingCount > 0"
            xs
            data-test="payment-request-processing-count-badge"
          >
            {{ processingCount }}
          </WdsLabel>
        </WdsStack>
      </template>
      <div class="tw-mt-2">
        <PaymentRequestsProcessingTable
          :is-active-tab="activeTabKey === 'processing'"
          @payment-request-updated="handlePaymentRequestUpdated"
        />
      </div>
    </WdsTab>
    <WdsTab :tab-key="'payrolled'" title="Payrolled" data-test="payment-requests-payrolled-tab">
      <template #title>
        <WdsStack align-center gap-sm>
          {{ $t('Payrolled') }}
          <WdsLabel v-if="payrolledCount > 0" xs data-test="payment-request-payrolled-count-badge">
            {{ payrolledCount }}
          </WdsLabel>
        </WdsStack>
      </template>
      <div class="tw-mt-2">
        <PaymentRequestsPayrolledTable
          :is-active-tab="activeTabKey === 'payrolled'"
          @payment-request-updated="handlePaymentRequestUpdated"
        />
      </div>
    </WdsTab>
  </WdsTabs>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useQuery, useApolloClient } from '@vue/apollo-composable'
import { WdsTabs, WdsTab, WdsStack, WdsLabel } from '@worksome/wds-components'
import { graphql } from '@/gql'
import { $t } from '@/composables/translation'
import PageHeader from '@/components/PageHeader.vue'
import PaymentRequestsToBePayrolledTable from '@/components/PaymentRequests/Partner/PaymentRequestsToBePayrolledTable.vue'
import PaymentRequestsProcessingTable from '@/components/PaymentRequests/Partner/PaymentRequestsProcessingTable.vue'
import PaymentRequestsPayrolledTable from '@/components/PaymentRequests/Partner/PaymentRequestsPayrolledTable.vue'

const activeTabKey = ref('to-be-payrolled')
const PAYMENT_REQUEST_COUNTS = graphql(`
  query PaymentRequestCounts($accounts: [ID!]!) {
    paymentRequestNewCount: paymentRequests(
      accounts: $accounts
      statuses: [APPROVED]
      hasBatch: false
      isPayrolled: false
    ) {
      paginatorInfo {
        total
      }
    }
    paymentRequestInProgress: paymentRequests(
      accounts: $accounts
      hasBatch: true
      isPayrolled: false
    ) {
      paginatorInfo {
        total
      }
    }
    paymentRequestCompleted: paymentRequests(
      accounts: $accounts
      hasBatch: true
      isPayrolled: true
    ) {
      paginatorInfo {
        total
      }
    }
  }
`)

const { result: countsResult } = useQuery(
  PAYMENT_REQUEST_COUNTS,
  () => ({
    accounts: [currentAccount.globalId],
  }),
  {
    clientId: 'publicApi',
  },
)

const toBePayrolledCount = computed(
  () => countsResult.value?.paymentRequestNewCount.paginatorInfo.total ?? 0,
)
const processingCount = computed(
  () => countsResult.value?.paymentRequestInProgress.paginatorInfo.total ?? 0,
)
const payrolledCount = computed(
  () => countsResult.value?.paymentRequestCompleted.paginatorInfo.total ?? 0,
)

const { client } = useApolloClient('publicApi')

const handlePaymentRequestUpdated = async () => {
  // Use Apollo's refetchQueries to refresh all payment request related data
  await client.refetchQueries({
    include: [
      'PartnerPaymentRequestsToBePayrolledQuery',
      'PartnerPaymentRequestsProcessingQuery',
      'PartnerPaymentRequestsPayrolledQuery',
      'PaymentRequestCounts',
      'BatchFilterProcessingQuery',
      'BatchFilterPayrolledQuery',
    ],
  })
}
</script>

<style scoped>
/* Page-local alignment: constrain ActionBar to table width */
:deep(.graphql-table-globals-wrapper .tw-fixed) {
  left: 50%;
  transform: translateX(-50%);
  max-width: calc(100vw - 2rem);
}

/* Match the ActionBar content width to the table content width */
:deep(.graphql-table-globals-wrapper .tw-w-\[800px\]) {
  width: 100%;
  max-width: 100%;
}
</style>
